import { useCurrentUser } from "context/UserContext";
import {
  FiAlertCircle,
  FiChevronLeft,
  FiFileText,
  FiGrid,
  FiHome,
  FiLogOut,
  FiPackage,
  FiShoppingBag,
} from "react-icons/fi";
import { NavLink } from "react-router-dom";

interface AdminSidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

interface NavItem {
  to: string;
  icon: typeof FiHome;
  label: string;
  end?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    label: "Overview",
    items: [
      { to: "/admin", icon: FiHome, label: "Dashboard", end: true },
    ],
  },
  {
    label: "Quotations",
    items: [
      { to: "/admin/quotations", icon: FiFileText, label: "Quotations" },
    ],
  },
  {
    label: "Products",
    items: [
      { to: "/admin/products/panels", icon: FiGrid, label: "Glass Panels" },
      { to: "/admin/products/attachments", icon: FiPackage, label: "Attachments" },
    ],
  },
  {
    label: "Stores",
    items: [
      { to: "/admin/stores", icon: FiShoppingBag, label: "Stores" },
    ],
  },
  {
    label: "Support",
    items: [
      { to: "/admin/bugs", icon: FiAlertCircle, label: "Bug Reports" },
    ],
  },
];

const AdminSidebar = ({
  collapsed,
  onToggleCollapse,
  mobileOpen,
  onCloseMobile,
}: AdminSidebarProps) => {
  const { currentUser, signOutCallToAction } = useCurrentUser();

  return (
    <>
      {mobileOpen && (
        <div
          className="admin-sidebar-overlay"
          onClick={onCloseMobile}
          aria-hidden="true"
        />
      )}
      <aside
        className={`admin-sidebar${mobileOpen ? " admin-sidebar--mobile-open" : ""}`}
      >
        <div className="admin-sidebar__header">
          {!collapsed && <span className="admin-sidebar__title">Admin</span>}
          <button
            className="admin-sidebar__collapse-btn"
            onClick={onToggleCollapse}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <FiChevronLeft
              className={`admin-sidebar__collapse-icon${collapsed ? " admin-sidebar__collapse-icon--rotated" : ""}`}
            />
          </button>
        </div>

        <nav className="admin-sidebar__nav">
          {navSections.map((section) => (
            <div key={section.label} className="admin-sidebar__section">
              {!collapsed && (
                <span className="admin-sidebar__section-label">
                  {section.label}
                </span>
              )}
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `admin-sidebar__link${isActive ? " admin-sidebar__link--active" : ""}`
                  }
                  onClick={onCloseMobile}
                >
                  <item.icon className="admin-sidebar__icon" />
                  {!collapsed && (
                    <span className="admin-sidebar__link-label">{item.label}</span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="admin-sidebar__footer">
          {currentUser && (
            <div className="admin-sidebar__user">
              {currentUser.photoURL && (
                <img
                  src={currentUser.photoURL}
                  alt=""
                  className="admin-sidebar__avatar"
                />
              )}
              {!collapsed && (
                <span className="admin-sidebar__user-name">
                  {currentUser.displayName}
                </span>
              )}
            </div>
          )}
          <button
            className="admin-sidebar__link admin-sidebar__signout"
            onClick={signOutCallToAction}
            aria-label="Sign out"
          >
            <FiLogOut className="admin-sidebar__icon" />
            {!collapsed && (
              <span className="admin-sidebar__link-label">Sign out</span>
            )}
          </button>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
